/**
 * loyalty-engine.js
 * Shared Rivo loyalty points calculation engine.
 * Non-module IIFE — no ES module imports, no importmap dependency.
 * Reads config from <script type="application/json" id="rivo-engine-config"> (injected by
 * snippets/rivo-engine-config.liquid) and attaches window.BWW.loyaltyEngine.
 *
 * Collection multipliers are now sourced from theme settings (not Rivo) and stamped
 * onto line items as _loyalty_multiplier by loyalty-atc-interceptor.js at add-to-cart time.
 *
 * @version 1.2.0
 */
(function () {
  "use strict";

  const DEFAULT_CONFIG = {
    pointsAmount: 1,
    currencyBaseAmount: 1,
  };

  /**
   * @constructor
   */
  function LoyaltyEngine(config) {
    this.config = config;
  }

  /**
   * Identifies Corso shipping/package protection lines that should be excluded from
   * qualifying cart price.
   * @param {object} item - cart.js line item object
   * @returns {boolean}
   */
  LoyaltyEngine.prototype.isShippingProtectionLine = function (item) {
    const title = String(item.title || "").toLowerCase();
    const vendor = String(item.vendor || "").toLowerCase();
    const type = String(item.product_type || "").toLowerCase();
    if (vendor.indexOf("corso") !== -1) return true;
    if (title.indexOf("shipping protection") !== -1 || title.indexOf("package protection") !== -1) return true;
    if (type.indexOf("protection") !== -1 && (type.indexOf("shipping") !== -1 || type.indexOf("package") !== -1))
      return true;
    return false;
  };

  /**
   * Sum final_line_price for cart lines excluding shipping protection add-ons.
   * @param {Array<object>} cartItems - array of cart.js line item objects
   * @returns {number} Total qualifying cents
   */
  LoyaltyEngine.prototype.cartQualifyingCents = function (cartItems) {
    if (!Array.isArray(cartItems)) return 0;
    const self = this;
    let sum = 0;
    for (let i = 0; i < cartItems.length; i++) {
      const item = cartItems[i];
      if (!item || typeof item !== "object") continue;
      if (self.isShippingProtectionLine(item)) continue;
      const line = Number(item.final_line_price);
      if (isFinite(line)) sum += line;
    }
    return sum;
  };

  /**
   * Calculate loyalty points for a given price.
   * Formula: Math.ceil(Math.floor(priceCents / 100) / currencyBaseAmount * pointsAmount * multiplier)
   *
   * @param {number} priceCents - Price in cents (e.g. 9999 for $99.99)
   * @param {number} [multiplier] - Points multiplier (1 = base rate, 3 = triple points). Defaults to 1.
   * @returns {number} Points earned (always a non-negative integer)
   */
  LoyaltyEngine.prototype.calculatePoints = function (priceCents, multiplier) {
    const cfg = this.config;
    const currencyBaseAmount = Number(cfg.currencyBaseAmount) > 0 ? Number(cfg.currencyBaseAmount) : 1;
    const pointsAmount = Number(cfg.pointsAmount) > 0 ? Number(cfg.pointsAmount) : 1;
    const mult = Number(multiplier) > 0 ? Number(multiplier) : 1;
    const wholeDollars = Math.floor(priceCents / 100);
    const result = Math.ceil((wholeDollars / currencyBaseAmount) * pointsAmount * mult);
    return result;
  };

  /**
   * Calculate total loyalty points for a cart by summing per-line-item points.
   * Each item carries its own multiplier read from the _loyalty_multiplier line property.
   * @param {Array<{cents: number, multiplier?: number}>} lineItems
   * @returns {number}
   */
  LoyaltyEngine.prototype.calculateCartPoints = function (lineItems) {
    if (!Array.isArray(lineItems) || lineItems.length === 0) return 0;
    const self = this;
    let total = 0;
    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item) continue;
      total += self.calculatePoints(Number(item.cents) || 0, item.multiplier);
    }
    return total;
  };

  // ─── Initialise ───────────────────────────────────────────────────────────

  const configEl = document.getElementById("rivo-engine-config");
  let config = DEFAULT_CONFIG;

  if (configEl) {
    try {
      const raw = JSON.parse(configEl.textContent || "{}");
      config = {
        pointsAmount: Number(raw.pointsAmount) > 0 ? Number(raw.pointsAmount) : 1,
        currencyBaseAmount: Number(raw.currencyBaseAmount) > 0 ? Number(raw.currencyBaseAmount) : 1,
      };
    } catch (_) {
      config = DEFAULT_CONFIG;
    }
  }

  window.BWW = window.BWW || {};
  window.BWW.loyaltyEngine = new LoyaltyEngine(config);

  document.dispatchEvent(new CustomEvent("loyaltyEngine:ready", { detail: { engine: window.BWW.loyaltyEngine } }));
})();
